import type { ClienteApi } from '../cliente';
import { claveIdempotencia, negocio } from '../comun';
import type { components, operations } from '../tipos/plataforma';

/** Operaciones de «Mi cuenta y seguridad» (1.8), de Identidad y Acceso, con su `operationId` y los tipos del contrato. */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Cuenta = Esquema<'Account'>;
export type SesionAbierta = Esquema<'Session'>;
export type ListaSesiones = Esquema<'SessionList'>;
export type FormasDeEntrar = Esquema<'SignInMethods'>;
export type IdentidadExterna = Esquema<'ExternalIdentity'>;
export type CodigosRecuperacion = Esquema<'RecoveryCodes'>;
export type AltaDosPasos = operations['startTwoFactorEnrollment']['responses'][200]['content']['application/json'];
export type CambiosCuenta = NonNullable<operations['updateMe']['requestBody']>['content']['application/json'];

export function operacionesCuenta(api: ClienteApi) {
    return {
        /** updateMe: nombre y apellidos o idioma. */
        updateMe: (cambios: CambiosCuenta, clave = claveIdempotencia()) => api.pedir<Cuenta>('PATCH', '/v1/me', { cuerpo: cambios, idempotencia: clave }),
        /** cancelEmailChange: anula el email pendiente; el enlace ya mandado deja de servir (G233). */
        cancelEmailChange: (clave = claveIdempotencia()) => api.pedir<undefined>('DELETE', '/v1/me/email-change', { idempotencia: clave }),
        /** changePassword: con la actual; cierra las demás sesiones. */
        changePassword: (actual: string, nueva: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('POST', '/v1/me/password', { cuerpo: { current_password: actual, new_password: nueva }, idempotencia: clave }),
        /** setMyPassword: crear la primera contraseña de una cuenta de Google o Apple (G103). */
        setMyPassword: (nueva: string, clave = claveIdempotencia()) => api.pedir<undefined>('PUT', '/v1/me/password', { cuerpo: { new_password: nueva }, idempotencia: clave }),
        /** startTwoFactorEnrollment: secreto y `otpauth://` para la aplicación de autenticación. */
        startTwoFactorEnrollment: (clave = claveIdempotencia()) => api.pedir<AltaDosPasos>('POST', '/v1/me/two-factor', { idempotencia: clave }),
        /** confirmTwoFactorEnrollment: el primer código; devuelve los 10 códigos de recuperación. */
        confirmTwoFactorEnrollment: (code: string, clave = claveIdempotencia()) =>
            api.pedir<CodigosRecuperacion>('POST', '/v1/me/two-factor/confirm', { cuerpo: { code }, idempotencia: clave }),
        /** disableTwoFactor: con un código de la aplicación. */
        disableTwoFactor: (code: string, clave = claveIdempotencia()) => api.pedir<undefined>('DELETE', '/v1/me/two-factor', { cuerpo: { code }, idempotencia: clave }),
        /** regenerateRecoveryCodes: los anteriores dejan de servir. */
        regenerateRecoveryCodes: (code: string, clave = claveIdempotencia()) =>
            api.pedir<CodigosRecuperacion>('POST', '/v1/me/two-factor/recovery-codes', { cuerpo: { code }, idempotencia: clave }),
        /** listMySessions: cada una con cómo se abrió (`sign_in_method`). */
        listMySessions: () => api.pedir<ListaSesiones>('GET', '/v1/me/sessions'),
        /** revokeMySession. */
        revokeMySession: (sessionId: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', `/v1/me/sessions/${encodeURIComponent(sessionId)}`, { idempotencia: clave }),
        /** revokeOtherSessions: cierra todas menos esta (G176). */
        revokeOtherSessions: (clave = claveIdempotencia()) => api.pedir<{ revoked_count: number }>('DELETE', '/v1/me/sessions', { idempotencia: clave }),
        /** listMySignInMethods: contraseña, dos pasos y proveedores vinculados (G109). */
        listMySignInMethods: () => api.pedir<FormasDeEntrar>('GET', '/v1/me/sign-in-methods'),
        /** setMyPin: el PIN de tablet en este negocio (4 a 6 cifras); elegir uno nuevo lo desbloquea (G174, G234). */
        setMyPin: (businessId: string, pin: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('PUT', `${negocio(businessId)}/members/me/pin`, { cuerpo: { pin }, idempotencia: clave }),
    };
}
